import React, { useState } from 'react';
import PSEPayment from './PSEPayment';
import CreditCardPayment from './CreditCardPayment';
import { paymentService } from '../../services/paymentService';
import analyticsService from '../../services/analyticsService';
import './PaymentContainer.css';

const PaymentContainer = ({
  amount = 50000,
  displayAmount,
  onPaymentSuccess,
  onPaymentError,
  products = [],
  shippingData = null,
  couponCode = '',
  guestInfo = null,
}) => {
  const shownAmount = displayAmount ?? amount;
  const [currentStep, setCurrentStep] = useState('method'); // method, pse, credit_card, result
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const paymentMethods = [
    {
      id: 'pse',
      name: 'PSE (Pagos Seguros en Línea)',
      description: 'Paga directamente desde tu banco',
      icon: '🏦',
      enabled: true,
      fees: 'Sin costo adicional'
    },
    {
      id: 'credit_card',
      name: 'Tarjeta de Crédito',
      description: 'Visa, Mastercard, American Express',
      icon: '💳',
      enabled: true,
      fees: 'Procesado por ePayco'
    },
    {
      id: 'daviplata',
      name: 'Daviplata',
      description: 'Pago con billetera digital',
      icon: '📱',
      enabled: false,
      fees: 'Próximamente'
    }
  ];

  const handleMethodSelect = (method) => {
    analyticsService.trackEvent('payment', 'method_selected', method.id, null, {
      amount: shownAmount,
    });
    setPaymentMethod(method);
    if (method.id === 'pse') {
      setCurrentStep('pse');
    } else if (method.id === 'credit_card') {
      setCurrentStep('credit_card');
    } else {
      analyticsService.trackEvent('payment', 'method_unavailable', method.id);
      alert('Este método de pago estará disponible próximamente');
    }
  };

  const handlePaymentSuccess = (result) => {
    setPaymentResult(result);
    setCurrentStep('result');
    onPaymentSuccess?.(result);
  };

  const handlePaymentError = (error) => {
    analyticsService.trackEvent('payment', 'attempt_failed', paymentMethod?.id || 'unknown', String(error), {
      amount: shownAmount,
    });
    setPaymentResult({ success: false, error });
    setCurrentStep('result');
    onPaymentError?.(error);
  };

  const handleBackToMethods = () => {
    // User selected a method then backed out — strong abandonment signal
    if (paymentMethod) {
      analyticsService.trackEvent('payment', 'method_abandoned', paymentMethod.id, null, {
        amount: shownAmount,
      });
    }
    setCurrentStep('method');
    setPaymentMethod(null);
  };

  const handleStartOver = () => {
    analyticsService.trackEvent('payment', 'retry', paymentMethod?.id || 'unknown', null, {
      amount: shownAmount,
    });
    setCurrentStep('method');
    setPaymentMethod(null);
    setPaymentResult(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const renderMethodSelection = () => (
    <div className="payment-container">
      <div className="payment-header">
        <h2>Métodos de Pago</h2>
        <div className="total-amount">
          <span>Total a pagar: </span>
          <strong>{formatCurrency(shownAmount)}</strong>
        </div>
      </div>

      {products.length > 0 && (
        <div className="order-summary">
          <h3>Resumen del Pedido</h3>
          <div className="products-list">
            {products.map((product, index) => (
              <div key={index} className="product-item">
                <span className="product-name">{product.name}</span>
                <span className="product-quantity">x{product.quantity}</span>
                <span className="product-price">{formatCurrency(product.price * product.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="payment-methods">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            className={`payment-method ${!method.enabled ? 'disabled' : ''}`}
            onClick={() => method.enabled && handleMethodSelect(method)}
          >
            <div className="method-icon">{method.icon}</div>
            <div className="method-details">
              <h4>{method.name}</h4>
              <p>{method.description}</p>
              <span className="method-fees">{method.fees}</span>
            </div>
            <div className="method-arrow">
              {method.enabled ? '→' : '🔒'}
            </div>
          </div>
        ))}
      </div>

      <div className="security-info">
        <div className="security-item">
          <span className="security-icon">🔒</span>
          <span>Transacciones 100% seguras</span>
        </div>
        <div className="security-item">
          <span className="security-icon">🛡️</span>
          <span>Datos protegidos con SSL</span>
        </div>
        <div className="security-item">
          <span className="security-icon">✅</span>
          <span>Certificado PCI DSS</span>
        </div>
      </div>
    </div>
  );

  const renderPSEPayment = () => (
    <div className="payment-container">
      <div className="payment-header">
        <button className="back-button" onClick={handleBackToMethods}>
          ← Volver
        </button>
        <h2>Pago PSE</h2>
      </div>
      
      <PSEPayment
        amount={amount}
        displayAmount={shownAmount}
        products={products}
        shippingData={shippingData}
        couponCode={couponCode}
        guestInfo={guestInfo}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onCancel={handleBackToMethods}
      />
    </div>
  );

  const renderResult = () => (
    <div className="payment-container">
      <div className="payment-result">
        {paymentResult?.success ? (
          <div className="result-success">
            <div className="result-icon">✅</div>
            <h2>¡Pago Exitoso!</h2>
            <p>Tu pago ha sido procesado correctamente.</p>
            
            {paymentResult.data && (
              <div className="payment-details">
                <div className="detail-item">
                  <span>Monto:</span>
                  <strong>{formatCurrency(shownAmount)}</strong>
                </div>
                {paymentResult.data.reference && (
                  <div className="detail-item">
                    <span>Referencia:</span>
                    <strong>{paymentResult.data.reference}</strong>
                  </div>
                )}
                <div className="detail-item">
                  <span>Método:</span>
                  <strong>PSE</strong>
                </div>
                <div className="detail-item">
                  <span>Estado:</span>
                  <strong>Procesando</strong>
                </div>
              </div>
            )}
            
            <div className="next-steps">
              <h4>Próximos pasos:</h4>
              <ul>
                <li>Serás redirigido al sitio web de tu banco</li>
                <li>Ingresa tus credenciales bancarias</li>
                <li>Confirma la transacción</li>
                <li>Recibirás un email de confirmación</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="result-error">
            <div className="result-icon">❌</div>
            <h2>Error en el Pago</h2>
            <p>{paymentResult?.error || 'Ocurrió un error procesando tu pago.'}</p>
            
            <div className="error-help">
              <h4>¿Qué puedes hacer?</h4>
              <ul>
                <li>Verifica los datos ingresados</li>
                <li>Asegúrate de tener fondos suficientes</li>
                <li>Intenta con otro banco</li>
                <li>Contacta a tu banco si el problema persiste</li>
              </ul>
            </div>
          </div>
        )}
        
        <div className="result-actions">
          <button 
            className="btn btn-primary" 
            onClick={handleStartOver}
          >
            Realizar Otro Pago
          </button>
          
          {paymentResult?.success && (
            <button 
              className="btn btn-secondary"
              onClick={() => window.location.href = '/'}
            >
              Volver al Inicio
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderCreditCardPayment = () => (
    <div className="payment-container">
      <div className="payment-header">
        <button className="back-button" onClick={handleBackToMethods}>
          ← Volver
        </button>
        <h2>Pago con Tarjeta de Crédito</h2>
      </div>
      
      <CreditCardPayment
        amount={amount}
        displayAmount={shownAmount}
        products={products}
        shippingData={shippingData}
        couponCode={couponCode}
        guestInfo={guestInfo}
        onSuccess={handlePaymentSuccess}
        onError={handlePaymentError}
        onCancel={handleBackToMethods}
      />
    </div>
  );

  // Render based on current step
  switch (currentStep) {
    case 'method':
      return renderMethodSelection();
    case 'pse':
      return renderPSEPayment();
    case 'credit_card':
      return renderCreditCardPayment();
    case 'result':
      return renderResult();
    default:
      return renderMethodSelection();
  }
};

export default PaymentContainer;
